import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Typography,
} from "@material-ui/core";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import ListIcon from "@mui/icons-material/ListOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Trash2Icon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MaskAndValid from "utils/maskAndValid";

export default function CartModal({
  open,
  onClose,
  currentBrand,
  families,
  orderState,
  isCartGrouped,
  toggleCartGrouping,
  removeFromCart,
  submitOrder,
  submitOrderLoading = false,
  businessRules,
  theme,
  vitrinesMeta = {},
}) {
  const [expandedAccords, setExpandedAccords] = useState(() => new Set());

  const cartData = useMemo(() => {
    const brandFamilies = families[currentBrand] || {};
    const groupedData = {};
    let subtotalTable = 0;
    let totalDiscount = 0;
    const vitrineKey = (prod, fam) => prod.vitrine != null ? prod.vitrine : fam.vitrine;

    Object.entries(orderState).forEach(([key, qty]) => {
      for (const [famKey, fam] of Object.entries(brandFamilies)) {
        for (const prod of fam.products || []) {
          if (key.includes(prod.id)) {
            const prefix = `${famKey}_${prod.id}_`;
            if (key.startsWith(prefix)) {
              const vKey = vitrineKey(prod, fam);
              const variant = key.substring(prefix.length);
              const grossPrice = prod.price;
              const discountPercent = prod.discount || 0;
              const netPrice = grossPrice * (1 - discountPercent / 100);
              const totalRow = netPrice * qty;
              const totalGrossRow = grossPrice * qty;

              subtotalTable += totalGrossRow;
              totalDiscount += totalGrossRow - totalRow;

              if (!groupedData[vKey]) groupedData[vKey] = {};
              if (!groupedData[vKey][fam.category])
                groupedData[vKey][fam.category] = {};
              if (!groupedData[vKey][fam.category][fam.subcategory])
                groupedData[vKey][fam.category][fam.subcategory] = {};
              if (!groupedData[vKey][fam.category][fam.subcategory][famKey]) {
                groupedData[vKey][fam.category][fam.subcategory][famKey] = {
                  famName: fam.name,
                  items: [],
                };
              }

              groupedData[vKey][fam.category][fam.subcategory][famKey].items.push({
                key,
                name: prod.name,
                ref: prod.ref,
                prodId: prod.id,
                variant,
                qty,
                grossPrice,
                netPrice,
                discountPercent,
                totalRow,
              });
              break;
            }
          }
        }
      }
    });

    return { groupedData, subtotalTable, totalDiscount };
  }, [orderState, currentBrand, families]);

  const toggleAccord = (accordId) => {
    setExpandedAccords((prev) => {
      const next = new Set(prev);
      if (next.has(accordId)) next.delete(accordId);
      else next.add(accordId);
      return next;
    });
  };

  const validationData = useMemo(() => {
    const data = {
      totalQty: 0,
      totalVal: 0,
      vitrines: {},
      categories: {},
    };
    const brandFamilies = families[currentBrand] || {};
    const vitrineKey = (prod, fam) => prod.vitrine != null ? prod.vitrine : fam.vitrine;

    Object.entries(orderState).forEach(([key, qty]) => {
      for (const [famKey, fam] of Object.entries(brandFamilies)) {
        for (const prod of fam.products || []) {
          if (key.includes(prod.id)) {
            const prefix = `${famKey}_${prod.id}_`;
            if (key.startsWith(prefix)) {
              const vKey = vitrineKey(prod, fam);
              let price = prod.price;
              if (prod.discount > 0) {
                price = prod.price * (1 - prod.discount / 100);
              }
              const val = qty * price;

              data.totalQty += qty;
              data.totalVal += val;

              if (!data.vitrines[vKey])
                data.vitrines[vKey] = { val: 0, qty: 0 };
              data.vitrines[vKey].val += val;
              data.vitrines[vKey].qty += qty;

              if (!data.categories[fam.category])
                data.categories[fam.category] = { val: 0, qty: 0 };
              data.categories[fam.category].val += val;
              data.categories[fam.category].qty += qty;
            }
          }
        }
      }
    });

    return data;
  }, [orderState, currentBrand, families]);

  const validationErrors = useMemo(() => {
    const errors = [];

    if (validationData.totalVal < businessRules.global.minOrderValue) {
      errors.push(
        `• Pedido Mínimo Global: R$ ${MaskAndValid.maskPrice(businessRules.global.minOrderValue)} (Faltam R$ ${MaskAndValid.maskPrice(
          businessRules.global.minOrderValue - validationData.totalVal
        )})`
      );
    }
    if (validationData.totalQty < businessRules.global.minOrderQty) {
      errors.push(
        `• Qtd Mínima Global: ${businessRules.global.minOrderQty} un.`
      );
    }

    for (const [vitrineKey, rules] of Object.entries(businessRules.vitrines || {})) {
      const current = validationData.vitrines[vitrineKey];
      if (current) {
        const label = rules.nome || vitrineKey;
        if (rules.minValue > 0 && current.val < rules.minValue) {
          errors.push(
            `• Vitrine ${label}: Mín R$ ${MaskAndValid.maskPrice(rules.minValue)} (Atual: R$ ${MaskAndValid.maskPrice(current.val)})`
          );
        }
        if (rules.minQty > 0 && current.qty < rules.minQty) {
          errors.push(
            `• Vitrine ${label}: Mín ${rules.minQty} un (Atual: ${current.qty})`
          );
        }
      }
    }

    for (const [cat, rules] of Object.entries(businessRules.categories)) {
      const current = validationData.categories[cat];
      if (current) {
        if (current.qty < rules.minQty) {
          errors.push(
            `• Categoria ${cat}: Mín ${rules.minQty} un (Atual: ${current.qty})`
          );
        }
      }
    }

    return errors;
  }, [validationData, businessRules]);

  const total = cartData.subtotalTable - cartData.totalDiscount;

  const getColorHex = (name) => {
    const map = {
      Branco: "#FFFFFF",
      Bianco: "#FFFFFF",
      Bege: "#F5F5DC",
      Nocciola: "#D2B48C",
      "Azul Naval": "#000080",
      Notte: "#191970",
      "Cinza Steel": "#778899",
      Marmo: "#E0E0E0",
      "Verde Eucalipto": "#556B2F",
      Argento: "#C0C0C0",
      "Rosa Malva": "#D8BFD8",
      Nero: "#000000",
      Rose: "#FF007F",
      Grafite: "#333333",
      Taupe: "#483C32",
      Natural: "#F0E68C",
      Vermelho: "#FF0000",
      "Bianco/Marinho": "#000080",
      "Bianco/Caqui": "#F0E68C",
    };
    return map[name] || "#eee";
  };

  if (Object.keys(orderState).length === 0) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        className="cart-modal-drawer"
        PaperProps={{
          className: "cart-modal-paper",
        }}
      >
        <Box className="cart-modal-container">
          <Box className="cart-modal-header">
            <Box className="cart-header-left">
              <ShoppingBagIcon className="cart-header-icon" style={{ color: theme.primary }} />
              <Typography 
                className="cart-header-title brand-font"
                style={{ color: theme.primary }}
              >
                Resumo
              </Typography>
            </Box>
            <Button onClick={onClose} className="close-btn">
              <CloseIcon />
            </Button>
          </Box>
          <Box className="cart-modal-content custom-scroll">
            <Box className="cart-items-list">
              <Box className="cart-empty-state">
                <ShoppingBagIcon className="empty-cart-icon" />
                <Typography className="empty-cart-text">Seu pedido está vazio</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="cart-modal-drawer"
      PaperProps={{
        className: "cart-modal-paper",
      }}
    >
      <Box className="cart-modal-container">
        <Box className="cart-modal-header">
          <Box className="cart-header-left">
            <ShoppingBagIcon className="cart-header-icon" style={{ color: theme.primary }} />
            <Typography className="cart-header-title brand-font" style={{ color: theme.primary }}>Resumo</Typography>
          </Box>
          <Box className="cart-header-right">
            <Button
              onClick={toggleCartGrouping}
              className="group-toggle-btn"
              startIcon={isCartGrouped ? <ListIcon /> : <LayersIcon />}
            >
              {isCartGrouped ? "Desagrupar" : "Agrupar"}
            </Button>
            <Button onClick={onClose} className="close-btn">
              <CloseIcon />
            </Button>
          </Box>
        </Box>

        <Box className="cart-modal-content custom-scroll">
          <Box className="cart-items-list">
            {Object.keys(cartData.groupedData)
              .sort()
              .map((vitrineKey) => {
                const rules = businessRules.vitrines?.[vitrineKey] || {};
                const vitrineMinVal = rules.minValue ?? 0;
                const vitrineMinQty = rules.minQty ?? 0;
                const vitrineLabel = vitrinesMeta[vitrineKey]?.nome ?? rules.nome ?? vitrineKey;
                const cats = cartData.groupedData[vitrineKey];
                let vitrineTotalVal = 0;
                let vitrineTotalQty = 0;

                Object.values(cats).forEach((subcats) =>
                  Object.values(subcats).forEach((familiesInSub) =>
                    Object.values(familiesInSub).forEach((fam) =>
                      fam.items.forEach((i) => {
                        vitrineTotalVal += i.totalRow;
                        vitrineTotalQty += i.qty;
                      })
                    )
                  )
                );
                const valOk = vitrineTotalVal >= vitrineMinVal;
                const qtyOk = vitrineTotalQty >= vitrineMinQty;

                return (
                  <Box key={vitrineKey} className="cart-vitrine-section">
                    <Box className="cart-vitrine-header">
                      <Typography className="vitrine-label">
                        VITRINE: {vitrineLabel}
                      </Typography>
                      <Box className="vitrine-header-right">
                        {vitrineMinVal > 0 && (
                          <Box
                            className={`validation-badge ${
                              valOk ? "val-success" : "val-error"
                            }`}
                          >
                            Mín. R$ {MaskAndValid.maskPrice(vitrineMinVal)}
                          </Box>
                        )}
                        {vitrineMinQty > 0 && (
                          <Box
                            className={`validation-badge ${
                              qtyOk ? "val-success" : "val-error"
                            }`}
                            style={{ marginLeft: vitrineMinVal > 0 ? 8 : 0 }}
                          >
                            Mín. {vitrineMinQty} un
                          </Box>
                        )}
                        <Typography className="vitrine-total">
                          R$ {MaskAndValid.maskPrice(vitrineTotalVal)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box className="cart-vitrine-content">
                      {Object.keys(cats)
                        .sort()
                        .map((catName) => {
                          const subcats = cats[catName];
                          return (
                            <Box key={catName} className="cart-category-section">
                              <Box className="cart-category-header">
                                <Typography
                                  className="category-name"
                                  style={{ color: theme.primary }}
                                >
                                  {catName}
                                </Typography>
                              </Box>
                              {Object.keys(subcats)
                                .sort()
                                .map((subName) => {
                                  const familiesInSub = subcats[subName];
                                  let subTotalQty = 0;
                                  let subTotalVal = 0;
                                  const subColors = new Set();
                                  Object.values(familiesInSub).forEach((fam) => {
                                    fam.items.forEach((item) => {
                                      subTotalQty += item.qty;
                                      subTotalVal += item.totalRow;
                                      subColors.add(item.variant);
                                    });
                                  });
                                  const accordId = `sub-${vitrineKey}-${catName}-${subName}`.replace(/\s+/g, "-");
                                  const isSubExpanded = !expandedAccords.has(accordId);

                                  const subcategoryHeader = (
                                    <Box
                                      className="cart-accordion-header"
                                      onClick={() => !isCartGrouped && toggleAccord(accordId)}
                                      role="button"
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (!isCartGrouped && (e.key === "Enter" || e.key === " ")) {
                                          e.preventDefault();
                                          toggleAccord(accordId);
                                        }
                                      }}
                                      style={isCartGrouped ? { cursor: "default" } : {}}
                                    >
                                      <Box style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, flexWrap: "wrap" }}>
                                        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#374151" }}>
                                          {subName}
                                        </Typography>
                                        {!isCartGrouped && (
                                          <Typography
                                            component="span"
                                            style={{
                                              fontSize: 12,
                                              color: "#6b7280",
                                              backgroundColor: "white",
                                              border: "1px solid #e5e7eb",
                                              padding: "2px 8px",
                                              borderRadius: 9999,
                                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                            }}
                                          >
                                            R$ {MaskAndValid.maskPrice(subTotalVal)} em {subTotalQty} itens
                                          </Typography>
                                        )}
                                      </Box>
                                      {!isCartGrouped && (
                                        isSubExpanded ? (
                                          <ExpandLessIcon style={{ width: 20, height: 20, color: "#6b7280" }} />
                                        ) : (
                                          <ExpandMoreIcon style={{ width: 20, height: 20, color: "#6b7280" }} />
                                        )
                                      )}
                                    </Box>
                                  );

                                  const subcategoryContent = (
                                    <Box className="cart-accordion-content">
                                      {Object.values(familiesInSub).map((group, groupIdx) => {
                                if (isCartGrouped) {
                                  const productsInFamily = {};
                                  group.items.forEach((item) => {
                                    if (!productsInFamily[item.prodId]) {
                                      productsInFamily[item.prodId] = {
                                        name: item.name,
                                        ref: item.ref,
                                        totalQty: 0,
                                        totalVal: 0,
                                        variants: [],
                                      };
                                    }
                                    productsInFamily[item.prodId].totalQty += item.qty;
                                    productsInFamily[item.prodId].totalVal += item.totalRow;
                                    productsInFamily[item.prodId].variants.push(item);
                                  });

                                  return Object.values(productsInFamily).map(
                                    (prodGroup, idx) => {
                                      const groupAccordId = `grouped-${vitrineKey}-${catName}-${subName}-${groupIdx}-${idx}`.replace(/\s+/g, "-");
                                      const isGroupExpanded = !expandedAccords.has(groupAccordId);
                                      const numColors = new Set(prodGroup.variants.map((v) => v.variant)).size;

                                      return (
                                        <Box key={`${groupIdx}-${idx}`} className="cart-grouped-product">
                                          <Box
                                            className="cart-accordion-header grouped-product-header"
                                            onClick={() => toggleAccord(groupAccordId)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                toggleAccord(groupAccordId);
                                              }
                                            }}
                                          >
                                            <Box style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, flexWrap: "wrap" }}>
                                              <Box>
                                                <Typography className="grouped-product-name">
                                                  {prodGroup.name}
                                                </Typography>
                                                <Typography className="grouped-product-ref">
                                                  Ref: {prodGroup.ref}
                                                </Typography>
                                              </Box>
                                            </Box>
                                            <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                              <Box style={{ display: "flex", alignItems: "end", flexDirection: "column" }}>
                                                <Typography
                                                  className="grouped-product-total"
                                                  style={{ color: theme.primary, textAlign: "right" }}
                                                >
                                                  R$ {MaskAndValid.maskPrice(prodGroup.totalVal)}
                                                </Typography>
                                                <Typography
                                                  component="span"
                                                  style={{
                                                    fontSize: 12,
                                                    color: "#6b7280",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  em {prodGroup.totalQty} itens
                                                </Typography>
                                              </Box>
                                              {isGroupExpanded ? (
                                                <ExpandLessIcon style={{ width: 20, height: 20, color: "#6b7280" }} />
                                              ) : (
                                                <ExpandMoreIcon style={{ width: 20, height: 20, color: "#6b7280" }} />
                                              )}
                                            </Box>
                                          </Box>
                                          {isGroupExpanded && (
                                            <Box className="grouped-variants-list">
                                              {prodGroup.variants.map((item) => (
                                                <Box key={item.key} className="grouped-variant-item-container">
                                                  <Box className="grouped-variant-item">
                                                    {item.variant !== "Único" && (
                                                      <Box
                                                        className="variant-color-dot"
                                                        style={{
                                                          backgroundColor: getColorHex(item.variant),
                                                        }}
                                                      />
                                                    )}
                                                    <Typography className="variant-name">
                                                      {item.variant === "Único"
                                                        ? "Unitário"
                                                        : item.variant}
                                                    </Typography>
                                                    <Box className="cart-item-price">
                                                      {item.discountPercent > 0 && (
                                                        <Typography className="cart-item-price-old">
                                                          R$ {MaskAndValid.maskDecimal(item.grossPrice, 0)}
                                                        </Typography>
                                                      )}
                                                      <Typography className="cart-item-price-new">R$ {MaskAndValid.maskPrice(item.netPrice)}</Typography>
                                                    </Box>
                                                    <Typography className="variant-qty">
                                                      {item.qty}
                                                    </Typography>
                                                    <Typography className="variant-total">
                                                      R$ {MaskAndValid.maskPrice(item.totalRow)}
                                                    </Typography>
                                                  </Box>
                                                  <Button
                                                    onClick={() => removeFromCart(item.key)}
                                                    className="remove-item-btn"
                                                  >
                                                    <Trash2Icon className="remove-icon" />
                                                  </Button>
                                                </Box>
                                              ))}
                                            </Box>
                                          )}
                                        </Box>
                                    );
                                    }
                                  );
                                } else {
                                  return group.items.map((item) => (
                                    <Box key={item.key} className="cart-item">
                                      <Box className="cart-item-info">
                                        <Typography className="cart-item-name">
                                          {item.name}
                                        </Typography>
                                        <Typography className="cart-item-variant">
                                          {item.variant}
                                        </Typography>
                                      </Box>
                                      <Box className="cart-item-price">
                                        {item.discountPercent > 0 && (
                                          <Typography className="cart-item-price-old">
                                            R$ {MaskAndValid.maskDecimal(item.grossPrice, 0)}
                                          </Typography>
                                        )}
                                        <Typography className="cart-item-price-new">R$ {MaskAndValid.maskPrice(item.netPrice)}</Typography>
                                      </Box>
                                      <Typography className="cart-item-qty">{item.qty}</Typography>
                                      <Typography 
                                        className="cart-item-total"
                                        style={{ color: theme.primary }}
                                      >
                                        R$ {MaskAndValid.maskPrice(item.totalRow)}
                                      </Typography>
                                      <Button
                                        onClick={() => removeFromCart(item.key)}
                                        className="remove-item-btn"
                                      >
                                        <Trash2Icon className="remove-icon" />
                                      </Button>
                                    </Box>
                                  ));
                                 }
                              })}
                            </Box>
                          );

                          if (isCartGrouped) {
                            return (
                              <Box key={subName} className="cart-subcategory-section" style={{ marginBottom: 12 }}>
                                <Typography className="category-name" style={{ color: theme.primary, marginBottom: 8, marginLeft: 12 }}>
                                  {subName}
                                </Typography>
                                {subcategoryContent}
                              </Box>
                            );
                          }

                            return (
                              <Box key={subName} className="cart-subcategory-accordion">
                                {subcategoryHeader}
                                {isSubExpanded && subcategoryContent}
                              </Box>
                            );
                          })}
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Box>

        <Box className="cart-modal-footer">
          {validationErrors.length > 0 && (
            <Box className="validation-feedback">
              <Typography className="validation-title">
                Regras não atendidas:
              </Typography>
              {validationErrors.map((error, idx) => (
                <Typography key={idx} className="validation-error">
                  {error}
                </Typography>
              ))}
            </Box>
          )}
          <Box className="cart-totals">
            <Box className="cart-total-row">
              <Typography>Subtotal</Typography>
              <Typography>
                R$ {MaskAndValid.maskPrice(cartData.subtotalTable)}
              </Typography>
            </Box>
            {cartData.totalDiscount > 0.01 && (
              <Box className="cart-total-row discount">
                <Typography>Descontos</Typography>
                <Typography>
                  - R$ {MaskAndValid.maskPrice(cartData.totalDiscount)}
                </Typography>
              </Box>
            )}
            <Divider className="cart-total-divider" />
            <Box className="cart-total-row final">
              <Typography className="cart-total-label">Total Líquido</Typography>
              <Typography 
                className="cart-total-value brand-font"
                style={{ color: theme.primary }}
              >
                R$ {MaskAndValid.maskPrice(total)}
              </Typography>
            </Box>
          </Box>
          <Box className="cart-footer-actions">
            <Button onClick={onClose} className="continue-btn">
              Continuar
            </Button>
            <Button
              onClick={submitOrder}
              className="submit-order-btn"
              style={{ backgroundColor: theme.primary }}
              disabled={validationErrors.length > 0 || submitOrderLoading}
            >
              {submitOrderLoading ? (
                <CircularProgress size={22} color="inherit" style={{ marginRight: 8 }} />
              ) : (
                <SendIcon />
              )}
              {submitOrderLoading ? "Enviando..." : "Enviar Pedido"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
